import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import {
  preflightMic,
  readMicPermission,
  requestMicPermission,
  watchMicPermission,
  type MicIssue,
  type MicPreflightResult,
  type PronunciationCorrection,
  type UseVoiceSessionReturn,
  type VoiceMessage,
  type VoiceMessageCorrection,
} from '@orborix/voice-client';
import { boundedMicPreflight } from './boundedMicPreflight.js';
import { isAudioStuck } from '../../../lib/audioStuck.js';
import { fetchSessionTranscript, type TranscriptTurn } from '../../../lib/sessionsApi.js';
import {
  fetchSessionSummary,
  type SessionSummarySkillCredit,
} from '../../../lib/sessionSummaryApi.js';
import { useSfx } from '../../../lib/sfx/index.js';
import { toConvSeverity } from '../errorModel.js';
import { placeHistoryGameSolutions, type HistoryGameSolutions } from '../gameSolutions.js';
import { clearMicAutoAskDenied, markMicAutoAskDenied, micAutoAskDenied } from '../micAskMemory.js';

/**
 * Mapea el transcript de una sesión previa (turnos crudos) a burbujas del
 * historial visible. Cada turno expande a user + bot (los vacíos se filtran).
 * Marca todo como `historical` para que la UI lo atenúe y lo separe del turno
 * vivo.
 *
 * The learner's bubbles carry what the history's session detail shows: the
 * turn's corrections (so the thread's evaluation is honest — the positive
 * feedback below never stands alone among hidden errors), `historicalTurn`
 * (session, turn index and persisted corrections verdict), by which the app
 * looks up the turn's skill chip and clean-sentence ✓, and — for a
 * translate-it attempt — its round as `gameAttempt` (plan 47), which the
 * thread stamps with the same `gameTurnStampOf` as live. The corrections carry
 * no `turnIndex` on purpose: the bubble's vote would go to the LIVE session
 * (`sessionId` of the screen), which after "Continuar" is another session.
 */
export function transcriptToHistory(
  turns: ReadonlyArray<TranscriptTurn>,
  sessionId: string,
): VoiceMessage[] {
  const out: VoiceMessage[] = [];
  for (const t of turns) {
    if (t.userText && t.userText.trim().length > 0) {
      const corrections: VoiceMessageCorrection[] = [];
      const pronunciationCorrections: PronunciationCorrection[] = [];
      for (const c of t.corrections) {
        if (c.kind === 'grammar') {
          corrections.push({
            said: c.said,
            fix: c.fix,
            explanation: c.explanation,
            severity: toConvSeverity(c.severity),
          });
        } else {
          pronunciationCorrections.push({
            word: c.word,
            saidIpa: c.saidIpa ?? '',
            targetIpa: c.targetIpa ?? '',
            tip: c.tip ?? '',
            detail: null,
          });
        }
      }
      out.push({
        who: 'user',
        text: t.userText,
        historical: true,
        historicalTurn: {
          sessionId,
          turnIndex: t.turnIndex,
          correctionsVerdict: t.correctionsVerdict,
        },
        ...(corrections.length > 0 ? { corrections } : {}),
        ...(pronunciationCorrections.length > 0 ? { pronunciationCorrections } : {}),
        // A translate-it attempt keeps its round and persisted meaning verdict:
        // the thread stamps it with the same rule as live (plan 47).
        ...(t.gameRound
          ? {
              gameAttempt: {
                seed: t.gameRound.seed,
                round: t.gameRound.round,
                challenge: t.gameRound.challenge,
                attemptNo: t.gameRound.attemptNo,
                rematch: t.gameRound.rematch,
                verdict: t.gameRound.meaningVerdict,
                judgedText: t.gameRound.judgedText,
                band: t.gameRound.band,
              },
            }
          : {}),
      });
    }
    if (t.assistantText && t.assistantText.trim().length > 0) {
      out.push({ who: 'bot', text: t.assistantText, historical: true });
    }
  }
  return out;
}

/**
 * The seeded history's skill credits (the session summary's `skillCredits`,
 * the debrief's DB source), from which the thread rebuilds the historical
 * bubbles' chips (`selectTranscriptSkillChips`). One session: the history is
 * the transcript of `seedSessionId` only.
 */
export interface HistorySkillCredits {
  sessionId: string;
  credits: ReadonlyArray<SessionSummarySkillCredit>;
}

/**
 * A summary is `ready` only once the session's post-session analysis wrote it:
 * right after the learner ends a session and taps "Continuar", and always for
 * a session still live (a reload's reattach), it is pending. Until then the
 * chips come from the response's `partial.skillCredits` (the live credits,
 * already in the DB); the summary is polled a few times for the final word (a
 * nail the radar takes back), never blocking the thread.
 */
export const HISTORY_SUMMARY_ATTEMPTS = 4;
export const HISTORY_SUMMARY_RETRY_MS = 5_000;

/**
 * En qué anda el pedido de permiso del aviso de mic:
 * - `'idle'`: no se pidió todavía (o el último pedido se resolvió) → el botón
 *   ofrece activar el micrófono.
 * - `'asking'`: probe en vuelo (el prompt del browser puede estar abierto).
 * - `'retry'`: rebotó pero el browser TODAVÍA puede preguntar — el user
 *   descartó el prompt (permiso en `'prompt'`), o no hay mic conectado. La
 *   acción sigue siendo tocar el botón.
 * - `'blocked'`: el permiso quedó en `'denied'` → ningún `getUserMedia` va a
 *   promptear y el único camino son los ajustes del browser.
 *
 * La diferencia entre los dos últimos NO sale del error (un `NotAllowedError`
 * es el mismo para el descarte y para el bloqueo): sale de consultar el estado
 * del permiso. Sin eso el aviso mandaba a los ajustes al primer descarte,
 * cuando en realidad bastaba con volver a tocar el botón.
 */
export type MicRetryPhase = 'idle' | 'asking' | 'retry' | 'blocked';

export interface UseSessionLifecycleReturn {
  /**
   * Qué le pasa al mic, si algo (`null` = mic sano). NO bloquea la charla: la
   * sesión abre igual con el input en texto. Es la señal de "esta charla es
   * sólo escrita" para el resto de la pantalla (aviso + mic oculto).
   */
  micIssue: MicIssue | null;
  /** Estado del pedido de permiso disparado desde el aviso (ver `MicRetryPhase`). */
  micRetryPhase: MicRetryPhase;
  /**
   * Pide el mic DESDE UN GESTO del user (tap del botón del aviso) y, si lo
   * consigue, recupera la voz en ESTA MISMA charla — sin volver a entrar.
   */
  requestMic: () => void;
  /** La sesión NO abrió (fallo real del arranque, no del mic). */
  startError: string | null;
  /** El transcript previo ya está sembrado (o no hay nada que sembrar). */
  historyReady: boolean;
  /**
   * The seeded history's skill credits, once its summary is ready; `null`
   * without history, while the summary is pending, or when it failed.
   */
  historySkillCredits: HistorySkillCredits | null;
  /**
   * The seeded history's «No sé» / «Ver solución» answers (plan 47 Round 2),
   * placed on its bubbles; `null` without history or without any.
   */
  historyGameSolutions: HistoryGameSolutions | null;
}

/**
 * Ciclo de vida de la sesión de voz: start al entrar (tolerando el
 * double-mount de StrictMode) + stop al salir + siembra del historial al
 * retomar una charla. También es el dueño del mic: corre el pre-flight, decide
 * la degradación a texto y expone la RECUPERACIÓN (`requestMic`) que devuelve
 * la voz a la charla en curso cuando el user concede el permiso.
 *
 * El `start()` se GATEA hasta que las prefs de DB estén hidratadas
 * (`prefsReady`). Sin esto, el `moduleHints` inicial captura `settings`
 * en sus defaults (p.ej. phonetic=true) y abre la sesión con PA encendido
 * ANTES de que la hidratación lo apague — el primer turno corría phonos
 * aunque el user lo tuviera off. Esperar el GET (rápido, local) garantiza
 * que el handshake WS ya lleve el valor correcto.
 */
export function useSessionLifecycle(args: {
  sessionRef: RefObject<UseVoiceSessionReturn | null>;
  prefsReady: boolean;
  resumeSessionId: string | null;
  /**
   * Reattach tras F5/crash: el `sessionId` persistido de la charla que se cortó.
   * El hook reattachea solo al WS (lo lee en `start()`); acá lo usamos para
   * sembrar el historial VISIBLE (mismo flujo que "Continuar"), porque un reload
   * pierde el `location.state` y sin esto la pantalla queda vacía y "pensando"
   * esperando un opener del coach que —en un reattach— no llega.
   */
  reattachSessionId?: string | null;
  /**
   * No abrir la sesión todavía: la hoja de instrucciones de la charla asistida
   * está abierta y el coach saluda recién cuando el alumno la cierra (Emmanuel,
   * 19/10: el saludo de fondo mientras lee confunde).
   */
  holdStart?: boolean;
}): UseSessionLifecycleReturn {
  const { sessionRef, prefsReady, resumeSessionId, reattachSessionId, holdStart = false } = args;
  // Id del cual sembrar el historial: "Continuar" (resume) o reattach de F5.
  const seedSessionId = resumeSessionId ?? reattachSessionId ?? null;

  const [micIssue, setMicIssue] = useState<MicIssue | null>(null);
  const [micRetryPhase, setMicRetryPhase] = useState<MicRetryPhase>('idle');
  const askingMicRef = useRef(false);
  const [startError, setStartError] = useState<string | null>(null);
  // Al retomar/reattachear gateamos el start() hasta que el transcript anterior
  // esté sembrado, para que las burbujas viejas se vean ANTES del primer turno
  // vivo (y `seedHistory` no quede no-op por mensajes ya presentes). En sesiones
  // nuevas arranca en `true` (no hay nada que esperar).
  const [historyReady, setHistoryReady] = useState(!seedSessionId);
  const [historySkillCredits, setHistorySkillCredits] = useState<HistorySkillCredits | null>(null);
  const [historyGameSolutions, setHistoryGameSolutions] = useState<HistoryGameSolutions | null>(
    null,
  );
  const startedRef = useRef(false);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { play } = useSfx();

  useEffect(() => {
    if (!prefsReady || !historyReady || holdStart) return; // hidratación + historial + la hoja
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    if (!startedRef.current) {
      startedRef.current = true;
      const boot = async (): Promise<void> => {
        // Pre-flight compartido del mic ANTES de abrir el WS: el pipeline
        // adquiere el mic recién en el open del socket y traga el rechazo
        // (`void mic.start()`), así que sin este paso un permiso denegado
        // dejaba la sesión viva pero MUDA — y encima facturaba el opener TTS
        // que nadie iba a responder.
        //
        // La POLÍTICA de speak es DEGRADAR, no bloquear (reporte de campo
        // 2026-09-09): sin mic la charla igual abre, con el input fijado en
        // texto. La conversación escrita es un canal de primera acá (el
        // composer siempre tuvo campo de texto y el server sabe correr con el
        // STT en bypass), así que quedarse afuera por no tener mic era perder
        // la sesión entera por la mitad del producto. El `inputMode` va en el
        // `start()` para que el handshake ya viaje en texto: así el server no
        // arma STT y el cliente ni pide el mic (nada de prompt ni de mic
        // abierto al pedo).
        //
        // Pero el pedido automático es UNO SOLO por browser (decisión del
        // dueño, 2026-09-17): si ya rebotó una vez, las entradas siguientes
        // NO vuelven a promptear solas — abren en texto con el botón del
        // aviso, y el prompt sale de un tap deliberado. El motivo es el
        // auto-bloqueo de Chrome a los 3 descartes (ver `micAskMemory.ts`):
        // preguntar en cada apertura le quema al user sus tres oportunidades
        // en carteles que no pidió. La consulta del permiso va DESPUÉS del
        // flag (corto-circuito) para no pagarla en el caso normal, y sólo
        // saltea si el permiso no está ya concedido: si lo habilitó desde los
        // ajustes, el probe resuelve sin prompt y la voz vuelve sola.
        //
        // El probe va con tope: un servidor de audio trabado no puede dejar
        // la charla sin abrir (ver `boundedMicPreflight`).
        const skipAutoAsk = micAutoAskDenied() && (await readMicPermission()) !== 'granted';
        const pf: MicPreflightResult = skipAutoAsk
          ? { status: 'denied', error: new Error('pedido automático omitido (ya rebotó antes)') }
          : await boundedMicPreflight();
        if (pf.status === 'ok') {
          clearMicAutoAskDenied();
        } else {
          setMicIssue(pf.status);
          // Sólo el rechazo de PERMISO gasta prompts; un mic ausente no, y el
          // device puede volver en la próxima entrada — ese caso sigue
          // probando siempre.
          if (pf.status === 'denied') markMicAutoAskDenied();
        }
        // Al retomar/reattachear suena la reentrada cálida; en una charla
        // nueva, el swell de arranque. Post-pre-flight: que no suene debajo
        // del prompt de permiso del browser.
        play(seedSessionId ? 'resume' : 'session-start');
        await sessionRef.current?.start(pf.status === 'ok' ? undefined : { inputMode: 'text' });
        // Audio del sistema trabado: el coach también por escrito (el server no
        // sintetiza una voz que nadie va a escuchar).
        if (isAudioStuck()) sessionRef.current?.setIoMode({ input: 'text', output: 'text' });
      };
      void boot().catch(() => {
        setStartError('No pudimos iniciar la sesión. Revisá tu conexión y volvé a intentar.');
      });
    }
    return () => {
      stopTimerRef.current = setTimeout(() => {
        // Resolución sonora del cierre SÓLO si la sesión seguía viva: el cierre
        // server-side (`session.ended` → 'idle') ya sonó vía el router, y tras
        // una caída ('lost') ya sonó `connection-lost`. El timer 0 absorbe el
        // double-mount de StrictMode (el re-run limpia el timer antes de sonar).
        if (sessionRef.current?.connectionStatus === 'open') play('session-end');
        void sessionRef.current?.stop();
        startedRef.current = false;
        stopTimerRef.current = null;
      }, 0);
    };
  }, [prefsReady, historyReady, holdStart, sessionRef, play, seedSessionId]);

  // ─── Recuperar la voz sin volver a entrar ──────────────────────────────
  // El aviso de "esta charla va por escrito" explicaba cómo recuperar la voz
  // pero no la recuperaba: el user tenía que salir y volver a entrar (pedido
  // del dueño, card #315). Esto es la otra mitad: un pedido de permiso
  // disparado por un GESTO (el tap del botón del aviso — clave, porque el
  // pre-flight del arranque corre sin gesto y ahí un prompt descartado no
  // vuelve a aparecer) y, si sale OK, el re-armado del canal de voz en la
  // sesión VIVA con un `mode.switch` a input voz.
  //
  // Que ese switch alcance no es casual: el `start({inputMode:'text'})` deja
  // el STT del backend en bypass pero el processor igual está en la pipeline
  // con su conexión abierta (`setup()` no depende del modo), y del lado del
  // cliente `start()` ya creó el `MicCapture` (crearlo no pide permiso; lo
  // pide su `start()`). O sea: el único que faltaba era el permiso.
  const recoverVoiceInput = useCallback((): void => {
    const s = sessionRef.current;
    // El OUTPUT queda como estaba (el toggle de voz del coach es del user y
    // no tiene nada que ver con el mic); sólo movemos el INPUT a voz.
    if (s) s.setIoMode({ input: 'voice', output: s.ioMode.output });
    setMicIssue(null);
    setMicRetryPhase('idle');
    // El mic volvió: la próxima entrada vuelve a tener su pedido automático
    // (si algún día el user lo revoca, no arranca castigado por esta vez).
    clearMicAutoAskDenied();
    play('resume');
  }, [sessionRef, play]);

  const requestMic = useCallback((): void => {
    if (askingMicRef.current) return; // doble tap mientras el prompt está abierto
    askingMicRef.current = true;
    setMicRetryPhase('asking');
    void (async () => {
      const res = await requestMicPermission();
      askingMicRef.current = false;
      if (res.status === 'ok') {
        recoverVoiceInput();
        return;
      }
      // Rebotó: el issue puede haber CAMBIADO de causa (permiso ya dado pero
      // el device ocupado, p.ej.), así que se re-escribe además de la fase.
      setMicIssue(res.status);
      // ¿Rebotó porque el user descartó el prompt, o porque el sitio está
      // bloqueado? El error no lo dice; el estado del permiso sí. Con el
      // permiso todavía en 'prompt' (o sin Permissions API, o sin device) la
      // acción sigue siendo el botón — mandarlo a los ajustes ahí sería
      // mandarlo a donde no hay nada que tocar.
      const state = res.status === 'denied' ? await readMicPermission() : null;
      setMicRetryPhase(state === 'denied' ? 'blocked' : 'retry');
    })();
  }, [recoverVoiceInput]);

  // Permiso habilitado a mano en los ajustes del browser (el camino cuando ya
  // bloqueó el sitio y ningún `getUserMedia` promptea): la Permissions API nos
  // avisa y recuperamos la voz sola, sin cobrarle al user un tap más. El probe
  // acá no necesita gesto — con el permiso ya concedido no hay prompt que
  // mostrar. Best-effort: en browsers que no exponen el permiso de mic
  // (Firefox) el watcher es un no-op y queda el botón.
  useEffect(() => {
    if (micIssue !== 'denied') return;
    return watchMicPermission((state) => {
      if (state !== 'granted') return;
      void preflightMic().then((res) => {
        if (res.status === 'ok') recoverVoiceInput();
      });
    });
  }, [micIssue, recoverVoiceInput]);

  // ─── Sembrar el transcript anterior ("Continuar" o reattach de F5) ─────
  // Con `seedSessionId` (resume o reattach), traemos el transcript de esa sesión
  // y lo sembramos como burbujas históricas ANTES de abrir el WS (el start está
  // gateado por `historyReady`). El backend ya inyecta este contexto en el LLM
  // (resume.ts / reattach); esto es la continuidad VISUAL. Degradación: si el
  // fetch falla, arrancamos igual (sin historial pintado) en vez de trabar la
  // sesión — pero igual liberamos el gate para que el coach retome.
  useEffect(() => {
    if (!seedSessionId) return;
    const ctrl = new AbortController();
    fetchSessionTranscript(seedSessionId, ctrl.signal)
      .then((res) => {
        if (ctrl.signal.aborted) return;
        sessionRef.current?.seedHistory(transcriptToHistory(res.turns, seedSessionId));
        // Placed by the same turn expansion as the seeded bubbles (a prefix
        // of the thread, so the indexes hold).
        setHistoryGameSolutions(placeHistoryGameSolutions(res.turns, res.gameSolutions));
      })
      .catch(() => {
        // Best-effort: el coach igual tiene el contexto en el LLM.
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setHistoryReady(true);
      });
    return () => ctrl.abort();
  }, [seedSessionId, sessionRef]);

  // ─── The history's skill chips (its session summary) ───────────────────
  // Apart from the seed above and never gating it: the chips land on the
  // historical bubbles whenever the summary is there. A failure only costs the
  // chips (warned, never thrown into the thread).
  useEffect(() => {
    if (!seedSessionId) return;
    const ctrl = new AbortController();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const attempt = (n: number): void => {
      fetchSessionSummary(seedSessionId, ctrl.signal)
        .then((res) => {
          if (ctrl.signal.aborted) return;
          if (res.ready && res.summary) {
            setHistorySkillCredits({
              sessionId: seedSessionId,
              credits: res.summary.skillCredits ?? [],
            });
            return;
          }
          if (res.partial?.skillCredits) {
            setHistorySkillCredits({ sessionId: seedSessionId, credits: res.partial.skillCredits });
          }
          if (n + 1 < HISTORY_SUMMARY_ATTEMPTS) {
            timer = setTimeout(() => attempt(n + 1), HISTORY_SUMMARY_RETRY_MS);
          }
        })
        .catch((err: unknown) => {
          if (ctrl.signal.aborted) return;
          console.warn(
            '[history-chips] summary of the seeded session failed; no skill chips',
            seedSessionId,
            err,
          );
        });
    };
    attempt(0);
    return () => {
      ctrl.abort();
      if (timer) clearTimeout(timer);
    };
  }, [seedSessionId]);

  return {
    micIssue,
    micRetryPhase,
    requestMic,
    startError,
    historyReady,
    historySkillCredits,
    historyGameSolutions,
  };
}
